import React, { useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import './style.css';

const EXPERIENCE = [
  {
    company: 'Fin',
    location: 'London, UK',
    role: 'Product Engineer',
    period: 'Mar 2026 — Present',
    bullets: [
      'Recently joined — building and shipping product, helping Fin\'s customers test and deploy Fin across their support teams.',
    ],
    tech: [],
  },
  {
    company: 'Bolt',
    location: 'Tallinn, Estonia',
    role: 'Senior Software Engineer',
    period: 'Jul 2025 — Mar 2026',
    bullets: [
      'Spearheaded the Cashback feature in the Bolt Food app, coordinating requirements across 7+ teams — Engineering, Product, Finance, and Legal.',
      'Projected to save €1M+ annually by replacing standard discounts with cashbacks that expire after a set period, optimising unused liability.',
    ],
    tech: ['TypeScript', 'Node.js', 'SQL Server', 'React'],
  },
  {
    company: 'Bolt',
    location: 'Tallinn, Estonia',
    role: 'Software Engineer',
    period: 'Nov 2022 — Jul 2025',
    bullets: [
      'Led full-stack development of the Bolt Plus subscription model for Bolt Food, coordinating across engineering, product, and business teams.',
      'Drove the international rollout to 10+ countries — €220K revenue impact in Q1 2025 and a direct lift in user retention.',
      'Architected the Restaurant Portal item-discounts feature, driving €3M revenue within the first six months.',
    ],
    tech: ['TypeScript', 'Node.js', 'React'],
  },
  {
    company: 'Disney+ Hotstar',
    location: 'Bengaluru, India',
    role: 'Software Development Engineer II',
    period: 'Aug 2021 — Oct 2022',
    bullets: [
      'Cut application deploy time on Kubernetes by 40% by designing internal automation tooling.',
      'Maintained 99.95% service availability by integrating Grafana and Victoria Metrics into the monitoring stack.',
      'Integrated a SaaS logging platform with existing clusters for long-term log retention; upgraded multiple clusters with zero downtime.',
      'Took on-call during high-traffic events and mentored interns building an AWS access-management portal.',
    ],
    tech: ['Kubernetes', 'Docker', 'React', 'Ruby on Rails', 'Prometheus', 'Victoria Metrics', 'AWS'],
  },
  {
    company: 'Thoughtworks',
    location: 'Bengaluru, India',
    role: 'Application Developer',
    period: 'Jun 2019 — Aug 2021',
    bullets: [
      'Doubled an e-commerce client\'s revenue by building scalable microservices that enabled a move to a marketplace model.',
      'Optimised inefficient JPA queries with Entity Graphs, enabling backend services to handle 3000+ requests/second.',
      'Automated performance tests with Python + Locust to surface slow endpoints; added JUnit/Mockito integration tests to reduce bugs.',
      'Mentored new graduates through onboarding, code walkthroughs, and reviews.',
    ],
    tech: ['Java', 'Spring Boot', 'Angular', 'Microservices', 'PostgreSQL'],
  },
];


const SECTIONS = [
  { key: 'writing', label: 'Writing', href: '/' },
  { key: 'photos', label: 'Map', href: '/photos' },
  { key: 'lunch', label: 'Lunch', href: '/lunch' },
  { key: 'resume', label: 'Résumé', href: '/resume' },
];

function SiteNav() {
  return (
    <nav className="site-nav" aria-label="Sections">
      {SECTIONS.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 ? <span className="site-nav-sep" aria-hidden="true">·</span> : null}
          <NavLink
            to={s.href}
            end={s.href === '/'}
            className={({ isActive }) => 'site-nav-link' + (isActive ? ' active' : '')}
          >
            {s.label}
          </NavLink>
        </React.Fragment>
      ))}
    </nav>
  );
}

export default function ResumePage({ theme, onToggleTheme }) {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Résumé — Sandeep Jaiswal';
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="page page-cv">
      <header className="site-header">
        <Link className="wordmark" to="/" aria-label="Home">
          sandeep<span className="wordmark-dot">.</span>
        </Link>
        <div className="site-nav-wrap">
          <SiteNav />
        </div>
        <button
          className="theme-toggle"
          onClick={onToggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
        >
          {theme === 'dark' ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round">
              <circle cx="8" cy="8" r="3" />
              <path d="M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.3 3.3l1 1M11.7 11.7l1 1M3.3 12.7l1-1M11.7 4.3l1-1" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13.5 9.5A6 6 0 1 1 6.5 2.5a5 5 0 0 0 7 7z" />
            </svg>
          )}
        </button>
      </header>

      <main className="main-cv">
        <section className="cv-intro">
          <div className="cv-id">
            <h1 className="cv-name">Sandeep Jaiswal</h1>
            <p className="cv-role">
              <span className="cv-role-title">Product Engineer</span>
              <span className="cv-role-dot"> · </span>
              <span className="cv-role-loc">London, UK</span>
            </p>
            <p className="cv-contact">
              <a href="mailto:sandeepjswl123@gmail.com">sandeepjswl123@gmail.com</a>
              <span className="cv-contact-dot">·</span>
              <a href="https://sandeep.london" target="_blank" rel="noopener noreferrer">sandeep.london</a>
              <span className="cv-contact-dot">·</span>
              <a href="https://github.com/sandyjswl" target="_blank" rel="noopener noreferrer">github.com/sandyjswl</a>
            </p>
          </div>
          <button className="cv-download" onClick={() => window.print()}>
            <span>Save as PDF</span>
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 1.5v8M3.5 6.5L7 10l3.5-3.5M2 12.5h10" />
            </svg>
          </button>
        </section>

        <section className="cv-section">
          <h2 className="cv-section-title">Experience</h2>
          <ol className="cv-jobs">
            {EXPERIENCE.map((job, i) => (
              <li key={i} className="cv-job">
                <div className="cv-job-period">{job.period}</div>
                <div className="cv-job-main">
                  <div className="cv-job-head">
                    <span className="cv-job-role">{job.role}</span>
                    <span className="cv-job-sep"> · </span>
                    <span className="cv-job-co">{job.company}</span>
                    <span className="cv-job-loc">{job.location}</span>
                  </div>
                  <ul className="cv-bullets">
                    {job.bullets.map((b, j) => <li key={j}>{b}</li>)}
                  </ul>
                  {job.tech.length > 0 && (
                    <div className="cv-tech">
                      {job.tech.map((t, k) => <span key={k} className="cv-chip">{t}</span>)}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>

      </main>

      <footer className="site-footer">
        <div className="footer-row">
          <a href="https://github.com/sandyjswl" target="_blank" rel="noopener noreferrer">GitHub</a>
          <a href="https://www.linkedin.com/in/sandyjswl/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          <a href="https://www.instagram.com/sandyjswl" target="_blank" rel="noopener noreferrer">Instagram</a>
          <Link to="/photos">Map</Link>
          <a href="mailto:sandeepjswl123@gmail.com">Email</a>
        </div>
      </footer>
    </div>
  );
}
